import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { chatbotService } from '../../../services/chatbotService';
import { LogoUploader, S3Params } from '../../LogoUploader'; 
import { agentInstanceService } from '../../../services/agentService';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Checkbox, TextArea, TextField } from '../../ui/Field';
import { Surface } from '../../ui/Surface';

interface GeneralTabProps {
  chatbot: any;
}

const GeneralTab: React.FC<GeneralTabProps> = ({ chatbot }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [greeting, setGreeting] = useState('Hello');
  const [placeholder, setPlaceholder] = useState('Type here...');
  const [logo, setLogo] = useState<S3Params | null>(null); 
  const [isSaving, setIsSaving] = useState(false);

  const [agentInstances, setAgentInstances] = useState<any[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);

  useEffect(() => {
    if (!chatbot) return;
    setName(chatbot.name ?? '');
    setDescription(chatbot.description ?? '');
    setGreeting(chatbot.config?.greeting ?? 'Hello');
    setPlaceholder(chatbot.config?.placeholder ?? 'Type here...');
    setLogo(chatbot.s3ObjectData ?? null);

    const rawAgents = chatbot.agents ?? [];
    const ids = rawAgents.map((a: any) => a.agentInstanceId).filter(Boolean);
    setSelectedAgentIds(ids);
  }, [chatbot?.id, chatbot?.updatedAt, chatbot]);

  useEffect(() => {
    let mounted = true;
    const loadAgents = async () => {
      setAgentsLoading(true);
      try {
        const resp = await agentInstanceService.listAgentInstance({});
        const items = (resp as any).agentInstances ?? resp ?? [];
        const mapped = (items || []).map((it: any) => ({
          id: it.id,
          name: it.name ?? it.code ?? `Agent ${it.id}`,
          lastActive: it.updatedAt ? new Date(it.updatedAt).toLocaleString() : undefined,
          type: it.agentType?.name ?? it.agentType?.code ?? 'unknown',
        }));
        if (mounted) setAgentInstances(mapped);
      } catch (err) {
        console.error('Failed to load agent instances', err);
        if (mounted) setAgentInstances([]);
      } finally {
        if (mounted) setAgentsLoading(false);
      }
    };
    loadAgents();
    return () => {
      mounted = false;
    };
  }, []);

  const pickerAgents = useMemo(() => agentInstances || [], [agentInstances]);

  const save = async () => {
    if (!chatbot?.id) {
      toast.error('Missing chatbot id — cannot save.');
      return;
    }

    setIsSaving(true);
    try {
      const payload: any = {
        name,
        description,
        isActive: chatbot.isActive ?? true,
        state: chatbot.state ?? 'draft',
        version: chatbot.version ?? 1,
        config: {
          ...(chatbot.config ?? {}),
          greeting: greeting ?? '',
          placeholder: placeholder ?? '',
        },
        s3ObjectData: logo ?? undefined,
        ...(chatbot?.podSubscription ? {} : { agentInstanceIds: selectedAgentIds }),
      };

      const resp = await chatbotService.updateChatbot(encodeURIComponent(chatbot.id), payload);
      const updated = (resp as any)?.data ?? resp;

      if (updated) {
        setName(updated.name ?? name);
        setDescription(updated.description ?? description);
        setGreeting(updated.config?.greeting ?? greeting);
        setPlaceholder(updated.config?.placeholder ?? placeholder);
        setLogo(updated.s3ObjectData ?? logo);
      }

      toast.success('General settings saved.');
    } catch (err: any) {
      console.error('Failed to save general settings', err);
      toast.error(err?.message ?? 'Failed to save general settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm font-medium text-agentic-text-primary mb-1">
            Chatbot Name
          </label>
          <TextField tone="default-focus-primary"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border focus:ring-2"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-agentic-text-primary mb-1">
            Description
          </label>
          <TextArea tone="default-focus-primary"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 rounded-lg border focus:ring-2"
          />
        </div>

        {/* Logo Uploader */}
        <div>
          <LogoUploader
            label="Chatbot Logo"
            value={logo}
            onChange={(val) => setLogo(val)}
          />
        </div>

        {/* Assign Agents (same UX as Chatbots list create/edit). A pod's chatbot keeps the pod's agents. */}
        {chatbot?.podSubscription ? (
          <div>
            <label className="block text-sm font-medium text-agentic-text-primary mb-1">Agents</label>
            <div className="flex flex-wrap gap-2 rounded-xl border border-gray-200 bg-agentic-gray-50 px-3 py-2">
              {(chatbot.agents ?? []).map((a: any) => (
                <Badge tone="primary-soft" key={a.agentInstanceId} className="rounded-full px-2 py-1 text-xs">
                  {a.agentInstance?.name ?? a.agentInstanceId}
                </Badge>
              ))}
            </div>
            <p className="mt-1 text-xs text-agentic-text-muted">
              These agents come with your {chatbot.podSubscription.podName} subscription and can't be changed here.
            </p>
          </div>
        ) : (
        <div>
          <label className="block text-sm font-medium text-agentic-text-primary mb-1">
            Assign Agents
          </label>
          <div
            className="w-full rounded-xl border border-gray-200 focus-within:ring-2 focus-within:ring-agentic-primary-blue focus-within:border-transparent px-2 py-2 flex flex-wrap gap-2 relative cursor-text"
            tabIndex={0}
            onClick={() => setAgentPickerOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setAgentPickerOpen((v) => !v);
              }
            }}
            aria-expanded={agentPickerOpen}
            role="combobox"
            aria-haspopup="listbox"
          >
            {selectedAgentIds.length === 0 && (
              <span className="text-sm text-agentic-text-muted px-1 py-1">
                Select agents…
              </span>
            )}
            {selectedAgentIds.map((id) => {
              const agent = pickerAgents.find((a) => a.id === id);
              if (!agent) return null;
              return (
                <Badge tone="primary-soft"
                  key={id}
                  className="px-2 py-1 rounded-full text-xs inline-flex items-center gap-2"
                >
                  {agent.name} • Last updated: {agent.lastActive}
                  <Button tone="custom"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAgentIds(selectedAgentIds.filter((x) => x !== id));
                    }}
                    className="text-agentic-primary-blue hover:text-agentic-primary-blue-dark"
                    aria-label="Remove agent"
                  >
                    ×
                  </Button>
                </Badge>
              );
            })}
            <div className="flex-1 min-w-[200px]" />
            <Button tone="muted-text"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setAgentPickerOpen((v) => !v);
              }}
              className="absolute right-2 top-2 px-2 py-1"
              aria-label="Toggle agents"
            >
              ▾
            </Button>

            {agentPickerOpen && (
              <Surface tone="card-raised" className="absolute left-0 top-full mt-2 w-full max-h-64 overflow-y-auto border rounded-xl z-10">
                <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                  <TextField tone="default-focus-primary"
                    value={agentSearch}
                    onChange={(e) => setAgentSearch(e.target.value)}
                    placeholder="Search agents"
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  />
                </div>
                <div className="p-2">
                  {agentsLoading && (
                    <div className="p-2 text-sm text-agentic-text-secondary">
                      Loading agents...
                    </div>
                  )}
                  {!agentsLoading && pickerAgents.length === 0 && (
                    <div className="p-2 text-sm text-agentic-text-secondary">
                      No agents available
                    </div>
                  )}
                  {!agentsLoading &&
                    pickerAgents.length > 0 &&
                    pickerAgents
                      .filter((a) =>
                        a.name.toLowerCase().includes(agentSearch.toLowerCase())
                      )
                      .map((agent) => {
                        const checked = selectedAgentIds.includes(agent.id);
                        return (
                          <Button tone="custom"
                            type="button"
                            key={agent.id}
                            onClick={() => {
                              if (checked) {
                                setSelectedAgentIds(
                                  selectedAgentIds.filter((x) => x !== agent.id)
                                );
                              } else {
                                setSelectedAgentIds([...selectedAgentIds, agent.id]);
                              }
                            }}
                            className={`w-full text-left flex items-center justify-between px-2 py-2 rounded-lg hover:bg-gray-50 ${
                              checked ? 'bg-agentic-primary-blue/5' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox
                               
                                readOnly
                                checked={checked}
                                className="pointer-events-none"
                              />
                              <div>
                                <div className="text-sm font-medium text-agentic-text-primary">
                                  {agent.name}
                                </div>
                                <div className="text-xs text-agentic-text-secondary">
                                  Last updated: {agent.lastActive}
                                </div>
                              </div>
                            </div>
                            <span className="text-xs text-agentic-text-secondary">
                              {agent.type}
                            </span>
                          </Button>
                        );
                      })}
                </div>
                <div className="sticky bottom-0 bg-white p-2 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-agentic-text-secondary">
                    {selectedAgentIds.length} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <Button tone="outline"
                      type="button"
                      className="px-3 py-1 text-sm rounded-lg border"
                      onClick={() => setSelectedAgentIds([])}
                    >
                      Clear
                    </Button>
                    <Button tone="primary"
                      type="button"
                      className="px-3 py-1 text-sm rounded-lg hover:opacity-90"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAgentPickerOpen(false);
                      }}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              </Surface>
            )}
          </div>
        </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-agentic-text-primary mb-1">
              Greeting Message
            </label>
            <TextField tone="default-focus-primary"
              value={greeting}
              onChange={(e) => setGreeting(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border focus:ring-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-agentic-text-primary mb-1">
              Placeholder Text
            </label>
            <TextField tone="default-focus-primary"
              value={placeholder}
              onChange={(e) => setPlaceholder(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border focus:ring-2"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button tone="custom"
          onClick={save}
          disabled={isSaving}
          className={`px-4 py-2 rounded-lg text-white ${
            isSaving ? 'bg-gray-300 cursor-not-allowed' : 'bg-agentic-primary-blue hover:bg-agentic-primary-blue/90'
          }`}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default GeneralTab;
